/* global Syn */
import dlv from 'dlv';

let already_updated = false;

class Version0 {

	constructor() {
		this.init();
	}

	init() {
		return { primary: null, affinities: [0,0,0,0,0] };
	}

	parse(cookie_value) {
		let matches;
		if (!(matches = /^0([0-9a-z]|X)((?:[0-9a-z]{2})+)$/.exec(cookie_value))) {
			return null;
		}
		let primary = matches[1] === 'X' ? null : parseInt(matches[1], 36);
		let affstr = matches[2];
		let affinities = [];
		for (let i=0; i<affstr.length/2; i++) {
			affinities[i] = parseInt(affstr.substr(i*2,2), 36);
		}
		return { primary, affinities };
	}
}

class Version1 {
	constructor(affinities) {
		this.affinities = affinities;
	}
	init() {
		let v1 = {
			primary: null,
			affinities: [],
			group: Math.random() < 0.5 ? 0 : 1,
			interval: this.interval_cur()
		};
		for (let i=0; i<this.affinities.length; i++) {
			v1.affinities[i] = 0;
		}
		return v1;
	}

	interval_cur() {
		// Weeks since 1/4/2014 7:00:00 PM
		return Math.floor(((new Date()).getTime()/1000 - 1388880000) / (60*60*24*7));
	}

	upgrade_from_previous(v0) {
		let v1 = this.init();
		let affinity_map = [0, 1, 2, 3, 4];

		if (v0.primary !== null && v0.primary < affinity_map.length) {
			v1.primary = affinity_map[v0.primary];
		}

		for (let i=0; i<v0.affinities.length; i++) {
			let new_i = affinity_map[i];
			if (new_i === null) {
				continue;
			}
			v1.affinities[new_i] = v0.affinities[i];
		}

		return v1;
	}

	parse(cookie_value) {
		let matches;
		if (!(matches = /^1([0-9a-z]|X)([0-9a-z])([0-9a-z]{2})((?:[0-9a-z]{2})+)$/.exec(cookie_value))) {
			if (/NaN/.exec(cookie_value)) {
				cookie_value = cookie_value.replace('NaN','00');
				matches = /^1([0-9a-z]|X)([0-9a-z])([0-9a-z]{2})((?:[0-9a-z]{2})+)$/.exec(cookie_value);
			}
			else {
				return null;
			}
		}
		let primary = matches[1] === 'X' ? null : parseInt(matches[1], 36);
		let group = parseInt(matches[2], 36);
		let interval = parseInt(matches[3], 36);
		let affstr = matches[4];
		let affinities = [];

		for (let i=0; i<this.affinities.length; i++) {
			affinities[i] = parseInt(affstr.substr(i*2,2), 36);
			if (isNaN(affinities[i])) {
				affinities[i] = 0;
			}
		}
		return { primary, group, interval, affinities };
	}
}

export default class SynAff {

	/**
	 * affinity definitions
	 *   title: the human-readable name of the affinity (for omniture tracking)
	 *   valid_primary: whether the affinity can be marked as primary (for dcc targeting)
	 *   regex: URL patterns to which this affinity applies
	 *
	 * @TODO: May be worth moving this into config for Cube
	 *
	 * IMPORTANT: Changing positions of these or deleting these is a breaking change. Additions are fine.
	 */
	affinity_definitions = [
		{
			title: 'sports',
			valid_primary: true,
			regex: /^(https?:\/\/[^/]+)?\/sports\/?|\/vendor\/espn\/|\/category\/(sports|nfl|soccer|football|hockey|basketball|golf|baseball|auto)\/|\/article\/(fox_sports|sports_illustrated|the_fumble|reuters_sports|cine_sport|oneup|golfcom|wwe|golf_digest|gamespot)/i
		},
		{
			title: 'entertainment',
			valid_primary: true,
			regex: /^(https?:\/\/[^/]+)?\/(entertainment|comics)\/?|\/category\/entertainment\//i
		},
		{
			title: 'general news',
			valid_primary: true,
			regex: /^(https?:\/\/[^/]+)?\/news\/?$|\/news\/(?:read\/)?category\/(?:us|news|world|politics|tech|business)\//i
		},
		{
			title: 'top searches',
			valid_primary: false,
			regex: /\bcontext=topsearches\b/i
		},
		{
			title: 'video',
			valid_primary: false,
			regex: /^(https?:\/\/[^/]+)?\/player\//i
		},
		{
			title: 'games',
			valid_primary: true,
			regex: /^(https?:\/\/[^/]+)?\/games\/?/i
		},
		{
			title: 'weather',
			valid_primary: true,
			regex: /^(https?:\/\/[^/]+)?\/weather\/?/i
		},
		{
			title: 'search',
			valid_primary: false,
			regex: /^(https?:\/\/[^/]+)?\/search\/?/i
		},
		{
			title: 'other',
			valid_primary: false,
			regex: /facebook.com|dpbolvw.net|youtube.com/i
		},
		{
			title: 'webmail',
			valid_primary: false,
			regex: /[./](web|z|e|)?mail[./]?/i
		},
		{
			title: 'finance',
			valid_primary: true,
			regex: /^(https?:\/\/[^/]+)?\/finance(?!\/category\/myportfolio)\/?|\/category\/finance\//i
		},
		{
			title: 'financeportfolio',
			valid_primary: false,
			regex: /^(https?:\/\/[^/]+)?\/finance\/category\/myportfolio\/?/i
		},
		{
			title: 'shopping',
			valid_primary: true,
			regex: /^(https?:\/\/[^/]+)?\/shopping\/?|amzn\.to|kqzyfj\.com|ebay\.com|jdoqocy\.com|amazon-adsystem\.com/i
		},
		{
			title: 'text',
			valid_primary: false,
			regex: /\/news\/read\/article\//i
		},
		{
			title: 'slideshow',
			valid_primary: false,
			regex: /^(https?:\/\/[^/]+)?\/slideshow\//i
		},
		{
			title: 'lifestyle',
			valid_primary: true,
			regex: /^(https?:\/\/[^/]+)?\/lifestyle\/|\/category\/lifestyle\//i
		}
	];

	/**
	 * group definitions
	 *   title: the human-readable name of the group (for omniture tracking)
	 */
	group_definitions = [
		{
			title: 'test'
		},
		{
			title: 'control'
		}
	];

	/**
	 * Methods for handling each version of the cookie
	 */
	version= [
		new Version0(),
		new Version1(this.affinity_definitions)
	];

	/**
	 * Return the string value of the synaff cookie or null if it does not exist or is empty
	 */
	_cookie_read = function() {
		let matches = /(?:^|;)\s*synaff\s*=\s*([^;]+)/.exec(document.cookie);
		if (matches) {
			return matches[1];
		}
		return null;
	};

	/**
	 * Read cookie value and return aff object
	 */
	read = function() {
		let synaff = this._cookie_read(), aff;

		if (synaff === null) {
			return null;
		}

		let version = parseInt(synaff.substr(0, 1), 36);
		if (!this.version[version] || typeof this.version[version].parse !== 'function') {
			return false;
		}
		aff = this.version[version].parse(synaff);
		if (!aff) {
			return false;
		}

		return this.upgrade(version, aff);
	};

	/**
	 * Read cookie, and map affinities to their category titles instead of just using indices
	 * @param {*} version_start
	 * @param {*} aff
	 */
	readAndLabel = function() {
		let val = this.read();
		return {
			...val,
			primary: dlv(this.affinity_definitions, `${val.primary}.title`), //map to primary title
			group: dlv(this.group_definitions, `${val.group}.title`), //map to group title
			affinities: val.affinities.reduce((acc, curr, i) => { //turn affinities into object from array, using indicies to find affinity group title
				acc[dlv(this.affinity_definitions, `${i}.title`)] = curr;
				return acc;
			}, {})
		};
	}

	/**
	 * Upgrade an aff object to the latest version
	 */
	upgrade = function(version_start, aff) {
		let version = version_start;
		while (this.version[version+1] && this.version[version+1].upgrade_from_previous) {
			aff = this.version[version+1].upgrade_from_previous(aff);
			version++;
		}
		aff.version_start = version_start;
		aff.version = version;
		return aff;
	};

	/**
	 * Update an aff object's primary affinity
	 */
	update_primary = function(aff) {
		let maxi = aff.primary;
		let maxv = aff.primary === null ? 0 : aff.affinities[aff.primary];
		for (let i=0; i<aff.affinities.length; i++) {
			if (this.affinity_definitions[i].valid_primary && aff.affinities[i] > maxv) {
				maxi = i;
				maxv = aff.affinities[i];
			}
		}
		aff.primary = maxi;
	};

	/**
	 * Update an aff object's interval and affinities by applying the halflife algorithm
	 */
	update_halflife = function(aff) {
		let cur_interval = this.version[this.version.length-1].interval_cur();
		// Make sure we haven't gone back in time
		// aff.interval should be smaller than the current interval
		if (aff.interval >= cur_interval) {
			return false;
		}

		// half life for each week since we last updated interval
		while (aff.interval < cur_interval) {
			for (let i=0; i<aff.affinities.length; i++) {
				if (aff.affinities[i] <= 2) {
					continue;
				}
				aff.affinities[i] = Math.ceil(aff.affinities[i]/2);
			}

			aff.interval++;
		}

		return true;
	};

	/**
	 * Base-36-encode an integer ("v") into a field of the given byte width ("bytes").
	 *
	 * If "v" is null, returns a field of width "bytes" filled with "X".  If "v"
	 * is negative, encodes 0.  If "v" is greater than the maximum value possible
	 * for the field, encodes the maximum value.  The field will be padded with
	 * zeroes to be of length "bytes".
	 */
	_b36_encode = function(v, bytes) {
		let str = '';
		if (v === null) {
			while (str.length < bytes) {
				str += 'X';
			}
			return str;
		}

		let max = Math.pow(36, bytes) - 1;
		str = (v > max ? max : v < 0 ? 0 : v).toString(36);
		while (str.length < bytes) {
			str = '0' + str;
		}
		return str;
	}

	/**
	 * Update the synaff cookie with the given value
	 */
	_cookie_write = function(cookie_value) {
		document.cookie = 'synaff='+cookie_value+'; path=/; domain='+location.hostname+'; expires=Tue, 19 Jan 2038 03:14:07 GMT';
	};

	/**
	 * Write aff object to cookies
	 */
	write = function(aff) {
		let cv = this._b36_encode(1,1) + this._b36_encode(aff.primary,1) + this._b36_encode(aff.group,1) + this._b36_encode(aff.interval,2);
		for (let i=0; i<aff.affinities.length; i++) {
			cv += this._b36_encode(aff.affinities[i], 2);
		}

		this._cookie_write(cv);
	};

	/**
	 * Return the affinity index for the given URL
	 */
	url_affinity = function(url) {
		for (let i=0; i<this.affinity_definitions.length; i++) {
			if (url.match(this.affinity_definitions[i].regex)) {
				return i;
			}
		}
		return null;
	};

	/**
	 * For stubbing in tests; returns the value of already_updated.
	 *
	 * @member Syn.Synaff
	 */
	_already_updated = function() {
		return already_updated;
	};

	/**
	 * For stubbing in tests; sets a dictionary of svalues for omniture
	 *
	 * @member Syn.Synaff
	 */
	_omniture_set = function(svalues) {
		// @TODO: Syn.Tracking.Omniture is technically a gen4 thing and may need to be changed in the future
		// Updated to also skip setting omniture if Syn.SynAff already exists, so it is only done once
		if (Syn.Tracking && Syn.Tracking.Omniture && Syn.Tracking.Omniture.setSValues && !Syn.SynAff) {
			Syn.Tracking.Omniture.setSValues(svalues);
		}
	};

	/**
	 * For stubbing in tests; returns the current URL.
	 */
	_url_current = function() {
		return location.href;
	};

	/**
	 * Read, update, and write the synaff cookie.  Can only be called once.
	 *
	 * @param {string} url null value for page render tracking, full url of target location if tracking external link.
	 */
	update = function(url) {
		let is_init = !this._already_updated();

		// disallow multiple invocations of this function only on page render
		// outgoing traffic is allowed multiple invocations!
		if (url === null && this._already_updated()) {
			return;
		}
		already_updated = true;

		let needs_write = false;
		let aff = this.read();

		if (aff === false) {
			// don't know how to handle cookie; it's probably from the future.  abort.
			return;
		}
		else if (aff === null) {
			// cookie was missing or blank.  create an aff object, but don't set needs_write unless we change it later.
			aff = this.version[this.version.length-1].init();
		}
		else {
			// cookie existed and was parsed.

			if (aff.version_start !== aff.version) {
				// the version was upgraded, so we should write out the new cookie.
				needs_write = true;
			}

			if (is_init) {
				if (this.update_halflife(aff)) {
					// the halflife algorithm made changes, so we should write out the new cookie.
					needs_write = true;
				}
			}
		}

		let url_affinity = this.url_affinity( url || this._url_current() );
		if (url_affinity !== null) {
			aff.affinities[url_affinity]++;
			this.update_primary(aff);
			needs_write = true;
		}

		this._omniture_set({
			prop46: aff.primary === null ? '(no affinity)' : this.affinity_definitions[aff.primary] ? this.affinity_definitions[aff.primary].title : '(unknown affinity)',
			prop47: this.group_definitions[aff.group] ? this.group_definitions[aff.group].title : '(unknown group)'
		});

		if (needs_write) {
			this.write(aff);
		}
	};

	/**
	 * Start the component.
	 *
	 * @param {Object} config The configuration data structure
	 */
	startup = function(config) {
		if (!config.skip_update) {
			this.update(null);
		}
	};
}



// WEBPACK FOOTER //
// ../node_modules/@cube/synaff/src/lib/synaff.js